/**
 * <strong>Purpose:</strong> <br>
 * Provides specific size calculations for drop down-like buttons.<br>
 *
 * <strong>Contract:</strong> <br>
 * This mixin is dependent upon: <br>
 * {@link module:mw-form/mixins/property/SizeMixin}<br>
 *
 * @module mw-form/mixins/property/DropDownButtonSizeMixin
 *
 * @copyright Copyright 2015-2016 The MathWorks, Inc.
 */

define([
        "dojo/_base/declare",
        "dojo/dom-geometry",
        "mw-form/mixins/property/SizeMixin"
    ],
    function (declare, domGeometry, SizeMixin) {

        var DropDownButtonSizeMixin =  declare(null, {

            minWidth: 0,
            maxWidth: Infinity,
            minHeight: 0,
            maxHeight: Infinity,
            doNotSize: false,

            /**
             * Override method from "SizeMixin" similar to "IconAndTextSizingMixin"
             * @param sizeType
             * @param value
             * @private
             */
            _setSize: function (/*String: width|height*/ sizeType, /*Number/String: units==="px"*/ value) {
                this.inherited(arguments);

                // Do not attempt DOM sizing during the widget construction phase
                if (!this._started || this.doNotSize) {
                    return;
                }

                var valueForDom = "";

                if (value !== "auto") {
                    var contentWidth;
                    var contentHeight;
                    var buttonValueForDomWidthOffset;
                    var dropDownValueForDomWidthOffset;
                    var buttonValueForDomHeightOffset;
                    var dropDownValueForDomHeightOffset;
                    var buttonValueForDomWidth;
                    var buttonValueForDomHeight;
                    var dropDownValueForDomWidth;
                    var dropDownValueForDomHeight;

                    var iconNodeMarginExtents = domGeometry.getMarginBox(this.get("iconNode"));
                    var textNodeMarginExtents = domGeometry.getMarginBox(this.get("textNode"));
                    var arrowNodeMarginExtents = domGeometry.getMarginBox(this.get("arrowNode"));
                    var borderWidth = 1;
                    valueForDom = parseFloat(this._getAdjustedSize(sizeType, value, this.domNode));

                    // Since drop down-like buttons can be in one of 4 states:
                    // (Block Horizontal, Inline Horizontal, Block Vertical, or Inline Vertical)
                    // And, each of these states require a slightly different calculation for size.
                    // And, the calculations required based on size types (height and width) are not
                    // going to be the same even within a single state.
                    // Then, the following switch yard is used to determine the button state, and
                    // provide the appropriate size calculations based on that state.
                    if ((sizeType === "width") &&
                        (this.get("iconAlignment") === "left") &&
                        (this.get("arrowMode") === "inline")) {
                        // Set 'width' of horizontal button with an inline arrow

                        // Calculate variables
                        contentWidth = iconNodeMarginExtents.w + arrowNodeMarginExtents.w;

                        if (valueForDom < contentWidth) {
                            buttonValueForDomWidth = (valueForDom / 2);
                            dropDownValueForDomWidth = (valueForDom / 2);
                        } else {
                            buttonValueForDomWidthOffset =
                                (contentWidth / 2) - iconNodeMarginExtents.w;
                            dropDownValueForDomWidthOffset =
                                (contentWidth / 2) - arrowNodeMarginExtents.w;

                            buttonValueForDomWidth =
                                (valueForDom / 2) - buttonValueForDomWidthOffset;
                            dropDownValueForDomWidth =
                                (valueForDom / 2) - dropDownValueForDomWidthOffset;
                        }

                        this._setDomStyles("width", buttonValueForDomWidth,
                            dropDownValueForDomWidth);
                    } else if ((sizeType === "height") &&
                        (this.get("iconAlignment") === "top") &&
                        ((this.get("arrowMode") === "inline") ||
                            (this.get("arrowMode") === "auto"))) {
                        // Set 'height' of vertical button with an inline arrow

                        // Calculate variables
                        contentHeight = iconNodeMarginExtents.h + textNodeMarginExtents.h +
                            arrowNodeMarginExtents.h + borderWidth;

                        if (contentHeight < 0) {
                            contentHeight = 0;
                        }

                        if (valueForDom < contentHeight) {
                            buttonValueForDomHeight = (valueForDom / 2);
                            dropDownValueForDomHeight = (valueForDom / 2);
                        } else {
                            buttonValueForDomHeightOffset =
                                (contentHeight / 2) - iconNodeMarginExtents.h;
                            dropDownValueForDomHeightOffset = (contentHeight / 2) -
                                (textNodeMarginExtents.h + arrowNodeMarginExtents.h);

                            buttonValueForDomHeight =
                                (valueForDom / 2) - buttonValueForDomHeightOffset + borderWidth;
                            dropDownValueForDomHeight =
                                (valueForDom / 2) - dropDownValueForDomHeightOffset - borderWidth;
                        }

                        this._setDomStyles("height", buttonValueForDomHeight,
                            dropDownValueForDomHeight);
                    } else if ((sizeType === "width") &&
                        (this.get("iconAlignment") === "left") &&
                        ((this.get("arrowMode") === "block") ||
                            (this.get("arrowMode") === "auto"))) {
                        // Set 'width' of horizontal button with a block arrow

                        buttonValueForDomWidth = (valueForDom - arrowNodeMarginExtents.w);
                        dropDownValueForDomWidth = arrowNodeMarginExtents.w;

                        this._setDomStyles("width", buttonValueForDomWidth,
                            dropDownValueForDomWidth);
                    } else if ((sizeType === "height") &&
                        (this.get("iconAlignment") === "top") &&
                        (this.get("arrowMode") === "block")) {
                        // Set 'height' of vertical button with a block arrow

                        buttonValueForDomHeight = (valueForDom - arrowNodeMarginExtents.h) - 3;
                        dropDownValueForDomHeight = arrowNodeMarginExtents.h + 1;

                        this._setDomStyles("height", buttonValueForDomHeight,
                            dropDownValueForDomHeight);
                    } else if (sizeType === "width") {
                        // Set 'width' of any button not previously handled
                        valueForDom =
                                (valueForDom < this.maxWidth ? valueForDom : this.maxWidth)
                            > this.minWidth ? valueForDom : this.minWidth;

                        this._setDomStyles("width", valueForDom, valueForDom);
                    } else if (sizeType === "height") {
                        // Set 'height' of any button not previously handled
                        valueForDom =
                                (valueForDom < this.maxHeight ? valueForDom : this.maxHeight)
                            > this.minHeight ? valueForDom : this.minHeight;

                        this._setDomStyles("height", valueForDom, valueForDom);
                    } else {
                        throw new Error("Invalid property value. The size type property expects one of these values: {'width', 'height', 'auto'}");
                    }
                } else {
                    this.domNode.style["min" + this._capitalize(sizeType)] = this["min" + this._capitalize(sizeType)] + "px";
                }

                this._set(sizeType, value);
            },

            _setDomStyles: function (sizeType, buttonValueForDom, dropDownValueForDom) {
                //"_getAdjustedSize" in "SizeMixin" assumes 'px' as units, so we will as well
                var buttonNodeAdjustment = 0;
                var focusNodeAdjustment = 0;
                var dropDownNodeAdjustment = 0;
                if (sizeType === "height") {
                    // TODO: Investigate why table-cell was used and see if we could use flexbox
                    // TODO: or something else instead to avoid these calculations.
                    // We only need the padding and border size while calculating height because the buttonNode and
                    // focusNode have "display: table-cell" and it has known issues with height calculations since
                    // it does not respect "box-sizing: border-box" property.
                    var nodeComputedStyle = window.getComputedStyle(this.domNode);
                    var borderAdjustment = parseFloat(nodeComputedStyle.getPropertyValue("border-bottom-width")) +
                        parseFloat(nodeComputedStyle.getPropertyValue("border-top-width"));

                    buttonNodeAdjustment = this._getPaddingBorderSizeForHeight(this.buttonNode) + borderAdjustment;
                    focusNodeAdjustment = this._getPaddingBorderSizeForHeight(this.focusNode);
                    dropDownNodeAdjustment = this._getPaddingBorderSizeForHeight(this.dropDownNode) + borderAdjustment;
                }

                this.buttonNode.style[sizeType] = (buttonValueForDom - buttonNodeAdjustment) + "px";
                this.buttonNode.style["max" + this._capitalize(sizeType)] = (buttonValueForDom -
                    buttonNodeAdjustment) + "px";

                this.dropDownNode.style[sizeType] = (dropDownValueForDom - dropDownNodeAdjustment -
                    focusNodeAdjustment) + "px";
                this.dropDownNode.style["max" + this._capitalize(sizeType)] = (dropDownValueForDom -
                    buttonNodeAdjustment - focusNodeAdjustment) + "px";
            },

            _getPaddingBorderSizeForHeight: function (node) {
                var nodeComputedStyle = window.getComputedStyle(node, null);
                return parseFloat(nodeComputedStyle.getPropertyValue("padding-top")) +
                    parseFloat(nodeComputedStyle.getPropertyValue("padding-bottom")) +
                    parseFloat(nodeComputedStyle.getPropertyValue("border-top-width")) +
                    parseFloat(nodeComputedStyle.getPropertyValue("border-bottom-width"));
            },

            _capitalize: function (string) {
                return string.charAt(0).toUpperCase() + string.slice(1);
            }

        });

        DropDownButtonSizeMixin._dependencies = [
            {mixin: SizeMixin, orderDependent: true}
        ];

        return DropDownButtonSizeMixin;

    });